import { ComponentProps } from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';

import { CloseIcon } from '@/assets/icons';
import { POPOVER_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

type PopoverProps = ComponentProps<typeof PopoverPrimitive.Root>;
type PopoverTriggerProps = ComponentProps<typeof PopoverPrimitive.Trigger>;
type PopoverContentProps = ComponentProps<typeof PopoverPrimitive.Content> & {
  showCloseButton?: boolean;
};
type PopoverAnchorProps = ComponentProps<typeof PopoverPrimitive.Anchor>;

const Popover = ({ ...props }: PopoverProps) => {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
};

const PopoverTrigger = ({ ...props }: PopoverTriggerProps) => {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
};

const PopoverContent = ({
  className,
  children,
  align = 'end',
  side = 'bottom',
  sideOffset = 8,
  showCloseButton,
  ...props
}: PopoverContentProps) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          'relative bg-custom-warm-grey-25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-full origin-(--radix-popover-content-transform-origin) rounded-[16px] outline outline-custom-warm-grey-200 p-6 shadow-custom-popover',
          className
        )}
        {...props}
      >
        {children}

        {showCloseButton && (
          <PopoverPrimitive.Close
            asChild
            className="absolute top-5 right-5 cursor-pointer text-custom-warm-grey-400"
          >
            <div
              data-testid={POPOVER_TEST_IDS.CLOSE_BUTTON}
              className="absolute top-5 right-5 cursor-pointer text-custom-warm-grey-400"
            >
              <CloseIcon width={20} height={20} />
            </div>
          </PopoverPrimitive.Close>
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
};

const PopoverAnchor = ({ ...props }: PopoverAnchorProps) => {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
};

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
